import { getDisplayAsset } from '@kwenta/sdk/utils'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import CalendarIcon from 'assets/svg/app/dashboard/calendar.svg'
import Button from 'components/Button'
import { DatePickerInput } from 'components/Input/DatePicker'
import Search from 'components/Table/Search'
import { FlexDiv, FlexDivRow } from 'components/layout/flex'
import { setDashboardHistoryTableFilter } from 'state/futures/reducer'
import {
	selectAccountOrderHistory,
	selectAccountTransfers,
	selectDashboardHistoryTableFilter,
	selectTradesHistoryTableData,
	selectUsersPositions,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { capitalize, createFilterOptions } from 'utils/futures'

import DropdownFilter, { type DropdownValue } from '../../../components/DropdownFilter'

import { type HistoryTab, HistoryTabValues, HistoryTabs } from './HistoryTabs'

const HistoryTableFilters = () => {
	const { t } = useTranslation()
	const router = useRouter()
	const dispatch = useAppDispatch()
	const positions = useAppSelector(selectUsersPositions)
	const trades = useAppSelector(selectTradesHistoryTableData, isEqual)
	const orders = useAppSelector(selectAccountOrderHistory)
	const transfers = useAppSelector(selectAccountTransfers)

	const activeTab = useMemo(() => {
		if (router.query.tab) {
			const tab = router.query.tab as HistoryTab
			if (HistoryTabValues.includes(tab)) {
				return tab
			}
		}
		return HistoryTabs.POSITIONS
	}, [router])

	const filterOptions = useMemo(() => {
		switch (activeTab) {
			case HistoryTabs.POSITIONS:
				return {
					assetsOptions: createFilterOptions(
						positions.map((p) => p.asset),
						getDisplayAsset
					),
					statusOptions: createFilterOptions(positions.map((p) => p.details.status)),
					sideOptions: createFilterOptions(
						positions.map((p) => p.details.side),
						capitalize
					),
				}
			case HistoryTabs.TRADES:
				return {
					assetsOptions: createFilterOptions(
						trades.map((t) => t.displayAsset || ''),
						getDisplayAsset
					),
					sideOptions: createFilterOptions(
						trades.map((p) => p.side ?? 'long'),
						capitalize
					),
				}
			case HistoryTabs.ORDER_HISTORY:
				return {
					assetsOptions: createFilterOptions(
						orders.map((o) => o.displayAsset || ''),
						getDisplayAsset
					),
					sideOptions: createFilterOptions(
						orders.map((o) => o.side),
						capitalize
					),
					statusOptions: createFilterOptions(orders.map((o) => o.status)),
					typeOptions: createFilterOptions(orders.map((o) => o.orderType)),
				}
			case HistoryTabs.TRANSACTION_HISTORY:
				return {
					assetsOptions: createFilterOptions(
						transfers.map((t) => t.asset || ''),
						getDisplayAsset
					),
					typeOptions: createFilterOptions(transfers.map((t) => t.action)),
				}
			default:
				return
		}
	}, [activeTab, positions, trades, orders, transfers])

	const dashboardHistoryFilters = useAppSelector(selectDashboardHistoryTableFilter(activeTab))

	const hasFilters = useMemo(() => {
		if (!dashboardHistoryFilters) {
			return false
		}
		return Object.values(dashboardHistoryFilters || {}).every((v) => !!v)
	}, [dashboardHistoryFilters])

	const handleDateChange = useCallback(
		(date: Date[]) => {
			dispatch(
				setDashboardHistoryTableFilter({
					historyTab: activeTab,
					tableFilter: {
						...dashboardHistoryFilters,
						from: date[0].toString(),
						to: date[1].toString(),
					},
				})
			)
		},
		[activeTab, dispatch, dashboardHistoryFilters]
	)

	const handleDropdownChange = useCallback(
		(dropdown: string, value: DropdownValue) => {
			dispatch(
				setDashboardHistoryTableFilter({
					historyTab: activeTab,
					tableFilter: { ...dashboardHistoryFilters, [dropdown]: value.value },
				})
			)
		},
		[activeTab, dashboardHistoryFilters, dispatch]
	)

	const handleResetFilter = useCallback(() => {
		dispatch(
			setDashboardHistoryTableFilter({
				tableFilter: null,
				historyTab: activeTab,
			})
		)
	}, [dispatch, activeTab])

	const handleSearchChange = useCallback(
		(value?: string) => {
			dispatch(
				setDashboardHistoryTableFilter({
					historyTab: activeTab,
					tableFilter: { ...dashboardHistoryFilters, search: value },
				})
			)
		},
		[activeTab, dashboardHistoryFilters, dispatch]
	)

	return (
		<Container>
			<DatePickerContainer>
				<DatePickerInput
					type="popover"
					label1=""
					prevLabel2="-"
					underline={false}
					date={{
						from: dashboardHistoryFilters?.from,
						to: dashboardHistoryFilters?.to,
					}}
					onChange={handleDateChange}
					anchorPosition="top"
					defaultActiveInput="from"
					iconTo={<CalendarIcon />}
				/>
			</DatePickerContainer>
			{filterOptions?.assetsOptions && (
				<DropdownFilter
					name="asset"
					options={filterOptions.assetsOptions}
					onChange={handleDropdownChange}
					isSearchable
					value={dashboardHistoryFilters?.asset}
				/>
			)}
			{filterOptions?.typeOptions && (
				<DropdownFilter
					name="type"
					options={filterOptions.typeOptions}
					onChange={handleDropdownChange}
					value={dashboardHistoryFilters?.type}
				/>
			)}
			{filterOptions?.sideOptions && (
				<DropdownFilter
					name="side"
					options={filterOptions.sideOptions}
					onChange={handleDropdownChange}
					value={dashboardHistoryFilters?.side}
				/>
			)}
			{filterOptions?.statusOptions && (
				<DropdownFilter
					name="status"
					options={filterOptions.statusOptions}
					onChange={handleDropdownChange}
					value={dashboardHistoryFilters?.status}
				/>
			)}
			{activeTab !== HistoryTabs.POSITIONS && (
				<SearchContainer>
					<Search
						placeholder={t('dashboard.history.search-placeholder')}
						value={dashboardHistoryFilters?.search || ''}
						onChange={handleSearchChange}
						onClear={() => handleSearchChange(undefined)}
					/>
				</SearchContainer>
			)}
			<Button onClick={handleResetFilter} fontSize={12} disabled={!hasFilters}>
				{t('dashboard.history.clear-filters')}
			</Button>
		</Container>
	)
}

const Container = styled(FlexDivRow)`
	position: relative;
	display: flex;
	margin-bottom: 20px;
	justify-content: flex-start;
	flex-wrap: wrap;
	gap: 10px;
	width: 100%;
`

const DatePickerContainer = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;

	.input-container {
		padding: 13px 10px;
		border: ${(props) => props.theme.colors.selectedTheme.border};
		border-radius: 8px;
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.containers.secondary.background};
	}
`

const SearchContainer = styled(FlexDiv)`
	max-width: 300px;
	min-width: 200px;
	height: 45px;
	input {
		font-size: 12px;
	}
`

export default HistoryTableFilters
